import { Link, useLoaderData, useParams } from 'react-router-dom'
import type { LoaderFunctionArgs } from 'react-router-dom'
import SEOHead, { buildBreadcrumbs, buildServiceSchema } from '../components/SEOHead'
import { slugify } from '../utils/slugify'
import type { Service } from '../types'

const SITE_URL = 'https://nala.vet'
const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api/v1'

// ─── SSG: tell vite-react-ssg which paths to pre-render ──────────────────────
export async function getStaticPaths(): Promise<string[]> {
  try {
    const res = await fetch(`${API_BASE}/services`)
    if (!res.ok) return []
    const services: Service[] = await res.json()
    return services.map((s) => `services/${slugify(s.name)}`)
  } catch {
    return []
  }
}

// ─── Loader: fetch service data at build time (SSG) & runtime ─────────────────
export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params as { slug: string }
  try {
    const res = await fetch(`${API_BASE}/services`)
    if (!res.ok) return { service: null }
    const services: Service[] = await res.json()
    const service = services.find((s) => slugify(s.name) === slug) ?? null
    return { service }
  } catch {
    return { service: null }
  }
}

// ─── Component ────────────────────────────────────────────────────────────────
export function Component() {
  const { slug } = useParams<{ slug: string }>()
  const loaderData = useLoaderData() as { service: Service | null } | null
  const service = loaderData?.service ?? null

  if (!service) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="text-5xl mb-4">🐾</p>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Service not found</h1>
        <p className="text-gray-500 mb-6">
          We couldn't find a service matching{' '}
          <span className="font-medium">{slug}</span>.
        </p>
        <Link to="/book" className="btn-primary">
          View all services
        </Link>
      </div>
    )
  }

  const serviceSlug = slugify(service.name)
  const canonicalUrl = `${SITE_URL}/services/${serviceSlug}`

  return (
    <>
      <SEOHead
        title={`${service.name} — In-Home Vet Service`}
        description={`${service.description} Performed at your home by a licensed Nala veterinarian. Starting at $${(service.price_cents / 100).toFixed(2)}.`}
        canonical={canonicalUrl}
        jsonLd={[
          buildServiceSchema({ ...service, slug: serviceSlug }),
          buildBreadcrumbs([
            { name: 'Book a Visit', url: `${SITE_URL}/book` },
            { name: service.name, url: canonicalUrl },
          ]),
        ]}
      />

      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 pt-8">
        <nav className="text-sm text-gray-400 flex items-center gap-2">
          <Link to="/" className="hover:text-brand-700">Home</Link>
          <span>/</span>
          <Link to="/book" className="hover:text-brand-700">Services</Link>
          <span>/</span>
          <span className="text-gray-600">{service.name}</span>
        </nav>
      </div>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="grid md:grid-cols-3 gap-10">
          {/* Main content */}
          <div className="md:col-span-2">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{service.name}</h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">{service.description}</p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">What's included</h2>
            <ul className="space-y-3 mb-8">
              {[
                'Full in-home consultation with a licensed veterinarian',
                'Nose-to-tail physical examination',
                'Personalized health recommendations and care plan',
                'Digital visit summary sent to your email',
                'Prescription dispatch to your preferred pharmacy if needed',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-600">
                  <span className="text-brand-600 mt-0.5 shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">How it works</h2>
            <ol className="space-y-4 mb-8">
              {[
                { step: '1', text: 'Create your account and add your pet\'s details.' },
                { step: '2', text: 'Book this service, choose a date, time, and enter your address.' },
                { step: '3', text: 'A licensed Nala vet arrives at your home with all necessary equipment.' },
                { step: '4', text: 'Receive a digital visit summary and any prescriptions by email.' },
              ].map(({ step, text }) => (
                <li key={step} className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-100 text-brand-700 font-bold text-sm flex items-center justify-center">
                    {step}
                  </span>
                  <p className="text-gray-600 pt-1">{text}</p>
                </li>
              ))}
            </ol>
          </div>

          {/* Booking card */}
          <div className="md:col-span-1">
            <div className="card sticky top-24">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{service.name}</h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-black text-brand-700">
                  ${(service.price_cents / 100).toFixed(2)}
                </span>
                <span className="text-sm text-gray-400">/ visit</span>
              </div>
              <div className="text-sm text-gray-500 mb-5 flex items-center gap-2">
                <span>⏱</span>
                <span>~{service.duration_minutes} minutes</span>
              </div>
              <Link
                to={`/register?service=${service.id}`}
                className="btn-primary w-full text-center block mb-3"
              >
                Book this visit
              </Link>
              <Link
                to="/book"
                className="btn-secondary w-full text-center block text-sm"
              >
                View all services
              </Link>
              <p className="text-xs text-gray-400 text-center mt-4">
                No hidden fees · Cancel up to 24h before
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Component
